import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation, useSearch } from 'wouter';
import { API_ERROR, MFA_CODE_MAX_LENGTH } from '../../shared/constants.ts';
import { ApiError, describeApiFailure } from '../api/http.ts';
import { clearSessionEnded, useSessionEnded } from '../auth/expiry.ts';
import { useLogin, useLoginMfa } from '../auth/session.ts';
import { useDocumentTitle } from '../hooks/use-document-title.ts';
import { AuthBrand } from './AuthBrand.tsx';
import styles from './LoginPage.module.css';

// Sign in: username and password, then, for an account with a second
// factor, the code from its authenticator app (or a recovery code) in the
// same form. The Worker keeps the half-done sign-in in a short challenge
// cookie; when that has run out, the form goes back to the first step and
// says so.

/** Only same-site absolute paths may be used as a post-login destination. */
export function safeNextPath(search: string): string {
  const next = new URLSearchParams(search).get('next');
  if (next !== null && next.startsWith('/') && !next.startsWith('//')) {
    return next;
  }
  return '/';
}

/** Spaces a code is pasted or read with; neither kind of code has any. */
const WHITESPACE = /\s+/gu;

/** Whether the code step failed because the challenge behind it is gone. */
function challengeExpired(error: unknown): boolean {
  return error instanceof ApiError && error.code === API_ERROR.CHALLENGE_EXPIRED;
}

interface CodeStepProps {
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSubmit: (code: string) => void;
  readonly onStartOver: () => void;
}

/** The second step: the app's code, or a recovery code instead. */
function CodeStep({ pending, error, onSubmit, onStartOver }: CodeStepProps) {
  const codeId = useId();
  const errorId = useId();
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(false);
  const fieldRef = useRef<HTMLInputElement>(null);

  // The step appears in place of the password fields: the cursor belongs in
  // its field, and again when the field changes kind.
  useEffect(() => {
    fieldRef.current?.focus();
  }, [recovery]);

  return (
    <form
      className={styles['card']}
      aria-describedby={errorId}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(code.replaceAll(WHITESPACE, ''));
      }}
    >
      <AuthBrand />
      <p className={styles['lead']} role="status">
        {recovery
          ? 'Enter one of your recovery codes. Each works once.'
          : 'Enter the code from your authenticator app.'}
      </p>
      <label className={styles['label']} htmlFor={codeId}>
        {recovery ? 'Recovery code' : 'Code'}
      </label>
      {recovery ? (
        <input
          ref={fieldRef}
          id={codeId}
          className={styles['input']}
          name="recovery-code"
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder="xxxx-xxxx"
          maxLength={MFA_CODE_MAX_LENGTH}
          required
          value={code}
          onChange={(event) => {
            setCode(event.target.value);
          }}
        />
      ) : (
        <input
          ref={fieldRef}
          id={codeId}
          className={styles['input']}
          name="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          value={code}
          onChange={(event) => {
            setCode(event.target.value);
          }}
        />
      )}
      <div id={errorId} role="alert" className={styles['error']}>
        {error}
      </div>
      <button className={styles['button']} type="submit" disabled={pending}>
        {pending ? 'Checking…' : 'Continue'}
      </button>
      <p className={styles['foot']}>
        <button
          className={styles['link-button']}
          type="button"
          onClick={() => {
            setCode('');
            setRecovery((previous) => !previous);
          }}
        >
          {recovery ? 'Use the authenticator app instead' : 'Use a recovery code instead'}
        </button>
      </p>
      <p className={styles['foot']}>
        <button className={styles['link-button']} type="button" onClick={onStartOver}>
          Start over
        </button>
      </p>
    </form>
  );
}

export function LoginPage() {
  const usernameId = useId();
  const passwordId = useId();
  const errorId = useId();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [codeStep, setCodeStep] = useState(false);
  const login = useLogin();
  const loginMfa = useLoginMfa();
  const [, navigate] = useLocation();
  const search = useSearch();
  const sessionEnded = useSessionEnded();
  useDocumentTitle('Sign in');

  const signedIn = () => {
    clearSessionEnded();
    navigate(safeNextPath(search), { replace: true });
  };

  const signIn = () => {
    login.mutate(
      { username, password },
      {
        onSuccess: ({ mfaRequired }) => {
          if (mfaRequired) {
            setPassword('');
            setCodeStep(true);
          } else {
            signedIn();
          }
        },
      },
    );
  };

  const startOver = () => {
    login.reset();
    loginMfa.reset();
    setCodeStep(false);
  };

  if (codeStep) {
    return (
      <main className={styles['page']}>
        <CodeStep
          pending={loginMfa.isPending}
          error={loginMfa.isError ? describeApiFailure(loginMfa.error) : null}
          onSubmit={(code) => {
            loginMfa.mutate(code, {
              onSuccess: signedIn,
              onError: (error) => {
                // The five minutes are up: the password is owed again, and
                // the message says so on the first step.
                if (challengeExpired(error)) {
                  setCodeStep(false);
                }
              },
            });
          }}
          onStartOver={startOver}
        />
      </main>
    );
  }

  const expired = loginMfa.isError && challengeExpired(loginMfa.error);
  return (
    <main className={styles['page']}>
      <form
        className={styles['card']}
        onSubmit={(event) => {
          event.preventDefault();
          loginMfa.reset();
          signIn();
        }}
        aria-describedby={errorId}
      >
        <AuthBrand />
        <p className={styles['lead']}>Sign in to your client map.</p>
        {sessionEnded ? (
          <p className={styles['lead']} role="status">
            Your session ended. Sign in again.
          </p>
        ) : null}

        <label className={styles['label']} htmlFor={usernameId}>
          Username
        </label>
        <input
          id={usernameId}
          className={styles['input']}
          name="username"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
          value={username}
          onChange={(event) => {
            setUsername(event.target.value);
          }}
        />

        <label className={styles['label']} htmlFor={passwordId}>
          Password
        </label>
        <input
          id={passwordId}
          className={styles['input']}
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />

        <div id={errorId} role="alert" className={styles['error']}>
          {login.isError ? describeApiFailure(login.error) : null}
          {expired ? describeApiFailure(loginMfa.error) : null}
        </div>

        <button className={styles['button']} type="submit" disabled={login.isPending}>
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </button>
        <p className={styles['foot']}>
          <Link href="/forgot" className={styles['link']}>
            Forgot your password?
          </Link>
        </p>
        <p className={styles['foot']}>Private app · sign in with your account.</p>
      </form>
    </main>
  );
}
